import React, { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import ConfirmationModal from 'containers/pages/projectDetails/components/ConfirmationModal';
import SkeletonLoader from 'containers/common/loaders/SkeletonLoader';
import SectionLoader from 'containers/common/loaders/SectionLoader';
import RequestsSkeleton from 'containers/pages/requests/components/RequestsSkeleton';
import DocumentsSkeleton from 'containers/pages/documents/components/DocumentsSkeleton';
import DocuSyncSkeleton from 'containers/pages/docuSync/components/DocuSyncSkeleton';
import DocuCheckSkeleton from 'containers/pages/docuCheck/components/DocuCheckSkeleton';
import ClientRequestsSkeleton from 'containers/pages/clientRequests/components/ClientRequestsSkeleton';
import MeetingsSkeleton from 'containers/pages/meetings/components/MeetingsSkeleton';
import TransactionsSkeleton from 'containers/pages/transactions/components/TransactionsSkeleton';
import TransactionalQuestionsSkeleton from 'containers/pages/transactionalQuestions/components/TransactionalQuestionsSkeleton';
import CalendarSkeleton from 'containers/pages/calendar/components/CalendarSkeleton';
import ContactsSkeleton from 'containers/pages/contacts/components/ContactsSkeleton';
import PendingRequestsSkeleton from 'containers/pages/pendingRequests/components/PendingRequestsSkeleton';
import SettingsSkeleton from 'containers/pages/settings/components/SettingsSkeleton';
import { useSelector } from 'react-redux';
import { GlobalContext } from 'context/GlobalContext';
import PublicRoutes from './PublicRoutes';
import PrivateRoutes from './PrivateRoutes';
import ProtectedRoutes from './ProtectedRoutes';

// AUTH PAGES
const LoginPage = lazy(() => import('containers/pages/login'));
const UniversalLoginPage = lazy(() => import('containers/pages/universalLogin'));
const ForgotPassword = lazy(() => import('containers/pages/forgotPassword'));
const ResetPassword = lazy(() => import('containers/pages/resetPassword'));
const ResetPasswordExpiry = lazy(() => import('containers/pages/invitationTokenExpiry'));

// PRIVATE PAGES
const Requests = lazy(() => import('containers/pages/requests'));
const ClientRequests = lazy(() => import('containers/pages/clientRequests'));
const Documents = lazy(() => import('containers/pages/documents'));
const TransactionalQuestions = lazy(() => import('containers/pages/transactionalQuestions'));
const ProjectDetails = lazy(() => import('containers/pages/projectDetails'));
const SettingsPage = lazy(() => import('containers/pages/settings'));
const Calendar = lazy(() => import('containers/pages/calendar'));
const Contacts = lazy(() => import('containers/pages/contacts'));
const Profile = lazy(() => import('containers/pages/profile'));
const SetPassword = lazy(() => import('containers/pages/setPassword'));
const PendingPasswordRequests = lazy(() => import('containers/pages/pendingRequests'));
const ClientRequestDetail = lazy(() => import('containers/pages/clientRequestDetail'));
const Transactions = lazy(() => import('containers/pages/transactions'));
const Meetings = lazy(() => import('containers/pages/meetings'));
const MeetingDetails = lazy(() => import('containers/pages/meetingDetails'));
const DocuSync = lazy(() => import('containers/pages/docuSync'));
const DocuCheck = lazy(() => import('containers/pages/docuCheck'));
const FolderDetails = lazy(() => import('containers/pages/folderDetails'));
const ContactSetPassword = lazy(() => import('containers/pages/contactSetPassword'));

// NOT FOUND ROUTE
const NotFound = lazy(() => import('containers/pages/not-found'));

function AppRoutes() {
  const [showModal, setShowModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [nextPath, setNextPath] = useState(null);

  const { user, hasSupportTicketsAccess, hasClientMeetingsAccess, hasDocuSyncAccess, hasDocuCheckAccess } =
    useSelector(state => state.auth);

  const handleToggleDialog = useCallback(() => setShowModal(prevState => !prevState), []);

  const contextValue = useMemo(
    () => ({
      showModal,
      setShowModal,
      selectedProject,
      setSelectedProject,
      nextPath,
      setNextPath,
    }),
    [showModal, selectedProject]
  );

  return (
    <GlobalContext.Provider value={contextValue}>
      <BrowserRouter>
        <Routes>
          <Route path="/">
            <Route path="auth" element={<PublicRoutes />}>
              <Route path="login" element={<Suspense fallback={<SectionLoader />}><LoginPage /></Suspense>} />
              <Route path="universal-login" element={<Suspense fallback={<SectionLoader />}><UniversalLoginPage /></Suspense>} />
              <Route path="forgot-password" element={<Suspense fallback={<SectionLoader />}><ForgotPassword /></Suspense>} />
              <Route path="reset-password" element={<Suspense fallback={<SectionLoader />}><ResetPassword /></Suspense>} />
              <Route path="invitation-token-expiry" element={<Suspense fallback={<SectionLoader />}><ResetPasswordExpiry /></Suspense>} />
            </Route>

            <Route path="/" element={<PrivateRoutes />}>
              <Route index element={<Suspense fallback={<RequestsSkeleton />}><Requests /></Suspense>} />

              <Route path="requests">
                <Route index element={<Suspense fallback={<RequestsSkeleton />}><Requests /></Suspense>} />
                <Route path=":taskID" element={<Suspense fallback={<SkeletonLoader />}><ProjectDetails /></Suspense>} />
              </Route>

              <Route
                path="meetings"
                element={(
                  <ProtectedRoutes
                    hasClientMeetingsAccess={
                      !!(user.has_client_meetings_access === 1 || hasClientMeetingsAccess === 1)
                    }
                  />
                )}
              >
                <Route index element={<Suspense fallback={<MeetingsSkeleton />}><Meetings /></Suspense>} />
                <Route path=":meetingID" element={<Suspense fallback={<SkeletonLoader />}><MeetingDetails /></Suspense>} />
              </Route>

              <Route path="settings" element={<Suspense fallback={<SettingsSkeleton />}><SettingsPage /></Suspense>} />
              <Route path="calendar" element={<Suspense fallback={<CalendarSkeleton />}><Calendar /></Suspense>} />
              <Route path="documents" element={<Suspense fallback={<DocumentsSkeleton />}><Documents /></Suspense>} />
              <Route path="transactional-questions" element={<Suspense fallback={<TransactionalQuestionsSkeleton />}><TransactionalQuestions /></Suspense>} />
              <Route
                path="client-requests"
                element={(
                  <ProtectedRoutes
                    hasSupportTicketsAccess={
                      !!(user.has_support_tickets_access === 1 || hasSupportTicketsAccess === 1)
                    }
                  />
                )}
              >
                <Route index element={<Suspense fallback={<ClientRequestsSkeleton />}><ClientRequests /></Suspense>} />
                <Route path=":id" element={<Suspense fallback={<SkeletonLoader />}><ClientRequestDetail /></Suspense>} />
              </Route>
              <Route path="transactions" element={<Suspense fallback={<TransactionsSkeleton />}><Transactions /></Suspense>} />
              <Route
                path="docu-sync"
                element={(
                  <ProtectedRoutes
                    hasDocuSyncAccess={!!(user.has_docsync_access === 1 || hasDocuSyncAccess === 1)}
                  />
                )}
              >
                <Route index element={<Suspense fallback={<DocuSyncSkeleton />}><DocuSync /></Suspense>} />
                <Route path=":folderID" element={<Suspense fallback={<SkeletonLoader />}><FolderDetails /></Suspense>} />
              </Route>
              <Route
                path="docu-check"
                element={(
                  <ProtectedRoutes
                    hasDocuCheckAccess={!!(user.has_docucheck_access === 1 || hasDocuCheckAccess === 1)}
                  />
                )}
              >
                <Route index element={<Suspense fallback={<DocuCheckSkeleton />}><DocuCheck /></Suspense>} />
              </Route>
              <Route path="contacts" element={<Suspense fallback={<ContactsSkeleton />}><Contacts /></Suspense>} />
              <Route path="profile" element={<Suspense fallback={<SkeletonLoader />}><Profile /></Suspense>} />
              <Route path="pending-access-requests" element={<Suspense fallback={<PendingRequestsSkeleton />}><PendingPasswordRequests /></Suspense>} />
              <Route path="set-password" element={<Suspense fallback={<SkeletonLoader />}><SetPassword /></Suspense>} />
              <Route path="contact-set-password" element={<Suspense fallback={<SkeletonLoader />}><ContactSetPassword /></Suspense>} />
            </Route>
          </Route>
          <Route path="*" element={<Suspense fallback={<SectionLoader />}><NotFound /></Suspense>} />
        </Routes>

        {showModal && (
          <ConfirmationModal open={showModal} onClose={handleToggleDialog} onConfirm={handleToggleDialog} />
        )}
      </BrowserRouter>
    </GlobalContext.Provider>
  );
}

export default AppRoutes;
